import React, { useEffect, useMemo, useReducer } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";

import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction } from "i18next";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { u2104AffilaitionRoleHandler } from "./U2104AffilaitionRoleHandlers";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { U2104AffiliationFormExtededHandlers } from "../../../U2104AffiliationForm";
import { NS, U2104ROLES } from "../../../../U2104";
import { emptyAffiliation, type U2104Affiliation, type U2104AffiliationRole } from "../../../../U2104Service";
import U2104AffilaitionRoleEditDlg from "./U2104AffilaitionRoleEditDlg";
import U2104AffilaitionRoleDeleteDlg from "./U2104AffilaiitonRoleDeleteDlg";

// Typage des props du composant
interface U2104AffilaitionRolesProps {
  organizationId: string;
  u2104AffiliationFormHandlers: U2104AffiliationFormExtededHandlers;
}

// Définition des colonnes pour le tableau des actions d'une unité logique
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "roleCode",
      label: t("user.lblRoleCode", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
    },
    {
      name: "roleDescription",
      label: t("user.lblRoleDescription", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
    },
  ];
}

// Composant principal pour la gestion des actions d'une unité logique
const U2104AffilaitionRolesBody: React.FC<U2104AffilaitionRolesProps> = ({ organizationId, u2104AffiliationFormHandlers }) => {
  // Translation
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const gridStateHandlers = useU2000GridStateHandlers<U2104AffiliationRole>();

  // Validation du formulaire pour les rôles d'affiliation
  const validateAffiliationRole = (_user: U2104Affiliation) => {
    const result: Record<string, string> = {};
    return result;
  };

  // Business logic pour les rôles d'affiliation
  const u2104AffilaitionRoleBL: U2000FormSBusinessHandlers<U2104Affiliation> = {
    newRecord: () => {
      return emptyAffiliation;
    },

    // validation du formulaire
    validateForm: validateAffiliationRole,
  };

  // Handlers pour les rôles d'affiliation
  const u2104AffilaitionRoleformStateHandlers = useU2000FormStateHandlers<U2104Affiliation>({
    formState,
    formStateDispatch,
    businessHandlers: u2104AffilaitionRoleBL,
  });

  // Handlers spécifiques pour les rôles d'affiliation
  const u2104AffilaitionRoleHandlers = u2104AffilaitionRoleHandler({
    u2000AxiosAuthenticated,
    gridStateHandlers: gridStateHandlers as unknown as ReturnType<typeof useU2000GridStateHandlers<U2104AffiliationRole>>,
    t,
    formStateHandlers: u2104AffilaitionRoleformStateHandlers,
    organizationId,
  });

  // Définition des colonnes du tableau
  const columns = useMemo(() => getColumnsDef(t), [t]);

  // Transposer les données du formStateHandlers au gridStateHandlers pour LogicalUnitActions
  useEffect(() => {
    // Initialiser les colonnes et les données du tableau
    const currentColumns = gridStateHandlers.getColumns?.() ?? [];
    if (JSON.stringify(currentColumns) !== JSON.stringify(columns)) {
      gridStateHandlers.setSize(9999);
      gridStateHandlers.setColumns(columns);
      gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
      gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));
    }

    // Initialiser les données du tableau
    const affiliationRoles = u2104AffiliationFormHandlers.getData?.()?.roles as U2104AffiliationRole[];
    gridStateHandlers.setData(Array.isArray(affiliationRoles) ? affiliationRoles : []);
    gridStateHandlers.setTotalRows(Array.isArray(affiliationRoles) ? affiliationRoles.length : 0);

    // Gérer les erreurs spécifiques au champ LogicalUnitActions
    const errorsField = u2104AffiliationFormHandlers.getOtherErrors()?.roleActions;
    if (errorsField !== undefined) {
      gridStateHandlers.setErrors(Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []);
    } else {
      gridStateHandlers.setErrors([]);
    }
  }, [u2104AffiliationFormHandlers, t]);

  // Mettre à jour le formStateHandlers parent lorsque les données du gridStateHandlers changent
  useEffect(() => {
    if (u2104AffilaitionRoleHandlers.dataChangedEvent === 0) return;

    const affiliationRoles = gridStateHandlers.getData();
    u2104AffiliationFormHandlers.setData({
      ...u2104AffiliationFormHandlers.getData?.(),
      roles: affiliationRoles,
    });
  }, [u2104AffilaitionRoleHandlers.dataChangedEvent]);

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableDeleteBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u2104AffiliationFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length === 0
      ? true
      : false || "resetPwd" in (u2104AffiliationFormHandlers.getCustomAttributes?.() ?? {});
  }

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = React.useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u2104AffilaitionRoleHandlers.onAdd,
        // changed to "not in" semantics: only true if hasRole AND resetPwd is NOT present
        hasRole: hasRole(U2104ROLES.ROLES),
        disabled: u2104AffiliationFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || "resetPwd" in (u2104AffiliationFormHandlers.getCustomAttributes?.() ?? {}),
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: u2104AffilaitionRoleHandlers.onDelete,
        hasRole: hasRole(U2104ROLES.ROLES),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u2104AffilaitionRoleHandlers]
  );

  // Render
  return (
    <>
      <U2104AffilaitionRoleEditDlg u2104AffilaitionRoleHandler={u2104AffilaitionRoleHandlers} t={t} NS={NS} />
      <U2104AffilaitionRoleDeleteDlg u2104AffilaitionRoleHandler={u2104AffilaitionRoleHandlers} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={u2104AffiliationFormHandlers.getFormState?.() === U2000FormStateEnumState.EDIT}
        label={t("user.lblUserRole", {
          count: u2104AffilaitionRoleHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U2104AffilaitionRolesBody;
